import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { X, Loader2 } from 'lucide-react'
import { createKnowledgeArea, deleteKnowledgeArea } from '../../../slice/sharepointSlice'

// Creates/manages Knowledge Areas — an independent tagging entity for
// SharePoint sites (NOT the same as Organization_Department / User Groups).
// Same simple "type a name, create it" pattern as AddCategoryModal.
const KnowledgeAreaManagerModal = ({ orgId, aispaceColor, t, onClose }) => {
  const dispatch = useDispatch()
  const { knowledgeAreas, createKnowledgeAreaLoading, createKnowledgeAreaError, deleteKnowledgeAreaLoading } = useSelector(state => state.sharepoint)
  const [name, setName] = useState('')
  const [error, setError] = useState('')

  const handleCreate = async () => {
    const trimmed = name.trim()
    if (!trimmed) {
      setError(t('manageOrganization.knowledgeAreaEmptyError'))
      return
    }
    if (knowledgeAreas.some(k => k.name.toLowerCase() === trimmed.toLowerCase())) {
      setError(t('manageOrganization.knowledgeAreaExistsError'))
      return
    }
    setError('')
    try {
      await dispatch(createKnowledgeArea({ orgId, name: trimmed })).unwrap()
      setName('')
    } catch (err) {
      setError(typeof err === 'string' ? err : t('manageOrganization.knowledgeAreaCreateError'))
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleCreate()
    }
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-[120]">
      <div className="bg-white rounded-2xl w-full max-w-[560px] overflow-hidden shadow-xl">
        <div className="relative px-8 pt-8 pb-6">
          <button onClick={onClose} className="absolute top-6 right-6 p-1 hover:bg-[#F3F4F6] rounded-lg transition-colors">
            <X size={20} className="text-[#6B7280]" />
          </button>
          <h2 className="text-[24px] font-semibold text-[#111827] text-center mb-3">{t('manageOrganization.knowledgeAreaManagerTitle')}</h2>
          <p className="text-[14px] text-[#6B7280] text-center max-w-[420px] mx-auto">{t('manageOrganization.knowledgeAreaManagerSubtitle')}</p>
        </div>

        <div className="px-8 pb-6">
          {(error || createKnowledgeAreaError) && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-[14px] text-red-600">
              {error || createKnowledgeAreaError}
            </div>
          )}

          <div className="mb-5">
            <label className="block text-[14px] font-semibold text-[#111827] mb-2">{t('manageOrganization.knowledgeAreaNameLabel')}</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={name}
                onChange={(e) => { setName(e.target.value); setError('') }}
                onKeyDown={handleKeyDown}
                placeholder={t('manageOrganization.knowledgeAreaNamePlaceholder')}
                className="flex-1 px-4 py-3 text-[14px] text-[#374151] border border-[#E5E7EB] rounded-xl focus:outline-none transition-colors placeholder:text-[#9CA3AF]"
                onFocus={(e) => e.target.style.borderColor = aispaceColor}
                onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                disabled={createKnowledgeAreaLoading}
              />
            </div>
            <p className="text-[12px] text-[#9CA3AF] mt-2">{t('manageOrganization.knowledgeAreaEnterHint')}</p>
          </div>

          {knowledgeAreas.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {knowledgeAreas.map((k) => (
                <span key={k.id} className="inline-flex items-center gap-2 px-4 py-2 text-[14px] text-[#374151] bg-[#F3F4F6] border border-[#E5E7EB] rounded-full">
                  {k.name}
                  <button
                    onClick={() => dispatch(deleteKnowledgeArea({ orgId, id: k.id }))}
                    disabled={deleteKnowledgeAreaLoading === k.id}
                    className="hover:text-[#111827] transition-colors disabled:opacity-50"
                  >
                    {deleteKnowledgeAreaLoading === k.id ? <Loader2 size={14} className="animate-spin" /> : <X size={16} />}
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="px-8 py-5 bg-[#F9FAFB] flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F3F4F6] transition-colors"
          >
            {t('common.close')}
          </button>
          <button
            disabled={createKnowledgeAreaLoading || !name.trim()}
            onClick={handleCreate}
            className="px-6 py-2.5 text-[14px] font-medium text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 min-w-[160px] justify-center"
            style={{ backgroundColor: aispaceColor }}
          >
            {createKnowledgeAreaLoading ? <Loader2 size={18} className="animate-spin" /> : <span>{t('manageOrganization.knowledgeAreaCreateButton')}</span>}
          </button>
        </div>
      </div>
    </div>
  )
}

export default KnowledgeAreaManagerModal
